import { useState, useEffect, useRef } from "react"
import { CaretRounded } from "@/common/icons/caret"
import Block from "@/components/wrappers/block"
import Magnifier from "@/common/icons/magnifier"
import styles from "./archive-ingredients.module.css"
import useStrings from "@/lib/hooks/useStrings"

export default function ArchiveIngredients({ block }) {
  const { list, options, tabs, empty } = block.attrs
  const [filteredList, setFilteredList] = useState([])
  const [sortBy, setSortBy] = useState("title")
  const [orderAsc, setOrderAsc] = useState(true)
  const [phrase, setPhrase] = useState("")
  const [selected, setSelected] = useState("")
  const [scroll, setScroll] = useState({ type: "", scroll: "" })
  const { noContent, searchPlaceholder, function: functionLabel } = useStrings()

  const stringInput = useRef()
  const tableHeaderRef = useRef()
  const itemsHeaderRef = useRef([])

  useEffect(() => {
    let newfilteredList = list
    //SELECT TYPE FILTER
    if (selected !== "") {
      newfilteredList = newfilteredList.filter((item) => item.key === selected)
    }

    //INPUT TEXT FILTER
    if (phrase !== "") {
      newfilteredList = newfilteredList.filter((item) => {
        return (
          item.title.toLowerCase().includes(phrase.toLowerCase()) ||
          item.supplier.toLowerCase().includes(phrase.toLowerCase()) ||
          item.CASnr.includes(phrase) ||
          item.chemical.toLowerCase().includes(phrase.toLowerCase())
        )
      })
    }

    //SORT
    newfilteredList.sort((a, b) => {
      if (!orderAsc) {
        return (
          b["type"].localeCompare(a["type"]) ||
          b[sortBy].localeCompare(a[sortBy])
        )
      }
      return (
        a["type"].localeCompare(b["type"]) || a[sortBy].localeCompare(b[sortBy])
      )
    })

    setFilteredList([...newfilteredList])
  }, [phrase, selected, sortBy, orderAsc, list])

  useEffect(() => {
    let timeoutId = null
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      if (itemsHeaderRef.current.length > 0) {
        itemsHeaderRef.current.forEach((el) => {
          if (!el) {
            return
          }
          el.style.paddingLeft = `${scroll.scroll}px`
        })
      }
      if (scroll.type === "body") {
        tableHeaderRef.current.scroll({
          left: scroll.scroll,
          behavior: "smooth"
        })
      }
    }, 200)
  }, [scroll])

  const doSelect = (e) => {
    setSelected(e.target.value)
  }

  const doSearch = () => {
    setPhrase(stringInput.current.value)
  }

  const doSort = (slug) => {
    if (sortBy === slug) {
      setOrderAsc(!orderAsc)
    } else {
      setOrderAsc(true)
    }
    setSortBy(slug)
  }

  const doScroll = (e, type) => {
    setScroll({ type: type, scroll: e.target.scrollLeft })
  }

  if (list.length === 0) {
    return (
      <Block block={block} className={styles.wrapper}>
        <p className="p-8 bg-good-green-20">{empty}</p>
      </Block>
    )
  }

  const TypeItem = ({ item, index }) => {
    const [hide, setHide] = useState(false)

    return (
      <div className="font-bold" data-filter-key={item.key}>
        <div
          className={`p-4 overflow-hidden border-b cursor-pointer bg-good-blue-5 border-gray-10 ${styles.typeHeader}`}
          onClick={() => {
            setHide(!hide)
          }}>
          <div
            className="flex items-center"
            ref={(el) => (itemsHeaderRef.current[index] = el)}>
            <CaretRounded
              className={`mr-5 ${hide ? styles.typeItemRotate : ""}`}
            />
            <h3>{item.type}</h3>
          </div>
        </div>
        <div>
          {!hide && (
            <div>
              {filteredList.map((listItem, index) => {
                if (listItem.type === item.type) {
                  return <ListItem key={`row-${index}`} item={listItem} />
                }
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  const ListItems = () => {
    const items = filteredList
      .filter(
        (listItem, index, self) =>
          self.findIndex(
            (i) => i.type === listItem.type && listItem.type !== ""
          ) === index
      )
      .map((item, index) => {
        return <TypeItem key={`type-${index}`} item={item} index={index} />
      })

    if (items.length === 0) {
      return <p className="px-4 py-8">{noContent}</p>
    }

    return items
  }

  const Options = () => {
    return options.map((item) => (
      <option key={item.value} value={item.value}>
        {item.label}
      </option>
    ))
  }

  const TableHeader = () => {
    if (tabs.length === 0) {
      return null
    }
    return (
      <div
        ref={tableHeaderRef}
        className="flex overflow-hidden cursor-pointer lg:grid-cols-4 grid-flow-column lg:grid whitespace-nowrap bg-good-blue-10">
        {tabs.map((tab) => {
          return (
            <div
              className={`flex flex-row items-center justify-between p-5 font-bold hover:bg-good-blue-25 ${
                tab.slug === sortBy ? "bg-good-blue-25" : ""
              } ${styles.tableHeaderTab}`}
              key={tab.slug}
              onClick={() => {
                doSort(tab.slug)
              }}>
              <span className="leading-4 whitespace-normal">{tab.label}</span>
              <span className="flex items-center">
                <CaretRounded
                  className={`caret pointer-events-none ${
                    !orderAsc && tab.slug === sortBy ? "rotate" : ""
                  }`}
                />
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Block block={block} className={styles.wrapper}>
      <div className="grid grid-cols-1 lg:grid-cols-6 lg:gap-x-5">
        <div className="grid grid-cols-1 col-span-6 lg:grid-cols-6 lg:gap-x-5 lg:mb-5">
          <div
            className={`flex items-center px-4 mb-5 border rounded lg:mb-0 border-black-100/20 h-15 lg:col-span-3 xl:col-span-4`}>
            <Magnifier className="w-5 mr-4 fill-current stroke-current" />
            <input
              type="search"
              placeholder={searchPlaceholder}
              className="w-full h-full text-lg focus:outline-none"
              onChange={doSearch}
              ref={stringInput}
            />
          </div>

          <div
            className={`lg:col-span-3 xl:col-span-2 relative block mx-0 font-sans text-lg font-normal text-left border rounded  border-black-100/20 h-15 mb-5 lg:mb-0`}>
            <label
              className="absolute z-10 font-sans text-xs font-bold leading-none tracking-widest pointer-events-none left-5 top-3"
              htmlFor="type-select">
              {functionLabel}
            </label>
            <select
              value={selected}
              onChange={doSelect}
              className="absolute block w-full h-full px-5 pt-3 text-sm outline-none appearance-none focus:outline-none">
              <Options />
            </select>
            <CaretRounded className="absolute right-0 mt-6 mr-4 pointer-events-none top-1" />
          </div>
        </div>
      </div>
      <TableHeader />
      <div
        className="overflow-x-scroll"
        onScroll={(e) => {
          const type = "body"
          doScroll(e, type)
        }}>
        <ListItems />
      </div>
    </Block>
  )
}

const ListItem = ({ item }) => {
  return (
    <ul className="flex grid-cols-4 py-5 font-normal border-b lg:grid border-black-100/10">
      <li className={styles.listItemItem}>{item.title}</li>
      <li className={styles.listItemItem}>{item.supplier}</li>
      <li className={styles.listItemItem}>{item.CASnr}</li>
      <li className={styles.listItemItem}>{item.chemical}</li>
    </ul>
  )
}
